import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../lib/auth";
import { isIOS } from "../lib/platform";
import { APP_REVIEW_LOCKDOWN } from "../lib/appReview";
import { toast } from "sonner";
import { X } from "lucide-react";

/**
 * Shared login sheet that shows BOTH login providers side-by-side.
 *
 * Required by App Store Review Guideline 4.8 — Sign in with Apple must be a
 * co-equal, top-level login option alongside Google. On iOS the Apple button
 * is rendered first; web users see only Google (native SIWA isn't supported
 * in WKWebView outside of Capacitor).
 */
export default function LoginSheet({ open, onClose, headline = "Sign in to continue" }) {
  const { signIn, signInWithApple } = useAuth();
  const [busy, setBusy] = useState(null); // "apple" | "google" | null
  const ios = isIOS();

  if (!open) return null;

  async function handleApple() {
    if (busy) return;
    setBusy("apple");
    try {
      await signInWithApple();
      onClose?.();
    } catch (e) {
      // T46/T48/T49 — `signInWithApple` attaches a multiline diagnostic
      // (route + full URL + status + bundled REACT_APP_BACKEND_URL +
      // detail) to `e.message` on HTTP errors. The full diagnostic is
      // always console-logged for Console.app tethered debugging.
      //
      // T49.F (Build 73) — Per user directive, do NOT hide backend
      // failures behind a friendly fallback. For 404 specifically
      // (route or gateway is wrong/stale — actionable!) we ALWAYS
      // show the verbose diagnostic so the user/App Review can
      // screenshot the exact URL the binary is calling. For other
      // HTTP failures, friendly text is fine in production unless
      // `localStorage.tiplingo_diag = '1'`.
      const fromAuth = typeof e?.message === "string" && e.message.includes("[/auth/apple]") ? e.message : null;
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      const verboseMsg = fromAuth || (typeof detail === "string" ? detail : null) || e?.message || "Sign in with Apple failed";
      console.error("[LoginSheet] T46/T49 handleApple error:", { msg: verboseMsg, code: e?.code, status });

      let diagOptIn = false;
      try {
        diagOptIn = typeof window !== "undefined" && window.localStorage?.getItem("tiplingo_diag") === "1";
      } catch { /* localStorage unavailable */ }

      const isHttpError = !!fromAuth || typeof status === "number";
      const isProd = process.env.NODE_ENV === "production";
      const is404 = status === 404 || /HTTP 404/.test(verboseMsg);
      // T49.W (Build 82) — During App Review lockdown ALL auth errors show
      // the verbose diagnostic so TestFlight users can screenshot the
      // exact URL + status + bundled REACT_APP_BACKEND_URL. The
      // "Sign-in is temporarily unavailable" friendly fallback previously
      // hid actionable info from users trying to debug why Build 81
      // couldn't sign in. Restored to friendly-first once App Review
      // approves and `APP_REVIEW_LOCKDOWN` is flipped off.
      const useFriendly = !APP_REVIEW_LOCKDOWN && isProd && isHttpError && !diagOptIn && !is404;
      const friendlyMsg = "Sign-in is temporarily unavailable. Please try again shortly.";
      const finalMsg = useFriendly
        ? friendlyMsg
        : (typeof verboseMsg === "string" ? verboseMsg : "Sign in with Apple failed");

      toast.error(finalMsg, {
        duration: useFriendly ? 4500 : 12000,
        // sonner respects newlines; pre-line keeps formatting in the
        // toast card so the screenshot is readable.
        style: { whiteSpace: "pre-line", maxWidth: "92vw" },
      });
    } finally {
      setBusy(null);
    }
  }

  function handleGoogle() {
    if (busy) return;
    setBusy("google");
    try {
      signIn();
      // signIn() either opens an in-app browser (iOS) or redirects (web);
      // either way, leave the sheet open so the user can come back without
      // a blocked UI if they cancel.
    } finally {
      setBusy(null);
    }
  }

  // Centering note: iOS WKWebView (Capacitor) occasionally mis-computes
  // `100vh` against the safe-area inset, which can drop a `flex
  // items-center` modal toward the home indicator. We sidestep that by
  // pinning the card with `top: 50%; left: 50%; transform: translate(-50%, -50%)`
  // — viewport-relative and immune to safe-area math. `max-h-[90vh]`
  // keeps very tall content scrollable instead of pushing off-screen.
  return createPortal(
    <div
      data-testid="login-sheet"
      className="fixed inset-0 z-[100] bg-navy/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        data-testid="login-sheet-card"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "calc(100% - 2rem)",
          maxWidth: "24rem",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        className="bg-bone rounded-3xl shadow-soft p-6 pb-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          data-testid="login-sheet-close"
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white flex items-center justify-center text-navy active:opacity-70"
          aria-label="Close sign in"
        >
          <X size={18} strokeWidth={2.25} />
        </button>

        <h2 className="font-jakarta font-bold text-xl text-navy mb-2 pr-10">
          {headline}
        </h2>
        <p className="font-jakarta text-sm text-navy-soft mb-6">
          Pick your preferred sign-in method. We only use your name and email.
        </p>

        {ios && (
          <button
            type="button"
            data-testid="apple-signin-btn"
            onClick={handleApple}
            disabled={busy !== null}
            className="w-full rounded-xl bg-black text-white font-jakarta font-bold text-base py-3 mb-3 flex items-center justify-center gap-2 active:opacity-80 disabled:opacity-60"
          >
            <svg width="16" height="18" viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
              <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
            </svg>
            {busy === "apple" ? "Signing in…" : "Sign in with Apple"}
          </button>
        )}

        <button
          type="button"
          data-testid="google-signin-btn"
          onClick={handleGoogle}
          disabled={busy !== null}
          className="w-full rounded-xl bg-white border border-navy/15 text-navy font-jakarta font-bold text-base py-3 flex items-center justify-center gap-2 active:opacity-80 disabled:opacity-60"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {busy === "google" ? "Opening…" : "Continue with Google"}
        </button>

        <p className="font-jakarta text-[11px] text-navy-soft/80 mt-5 text-center leading-relaxed">
          By continuing you agree to TipLingo's Terms of Use and Privacy Policy.
        </p>
      </div>
    </div>,
    document.body
  );
}
